import { useState } from 'react';
import { Box, Typography, Chip, Stack, TextField, Button } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';

export default function ChipListTab({
    title,
    field,
    encounter,
    onPatch,
    disabled,
    placeholder,
    hint,
    // Optional "none" quick-toggle. `denialLabel` is the button text the
    // clinician sees ("No current medication"); `denialValue` is the
    // readable phrase stored on the encounter and printed on the summary
    // ("No current medications"). When the section holds exactly this one
    // value it's treated as a negative assertion rather than data.
    denialLabel,
    denialValue,
}) {
    const items = encounter?.[field] || [];
    const isDenied = Boolean(denialValue) && items.length === 1 && items[0] === denialValue;
    const [draft, setDraft] = useState('');

    const add = () => {
        const v = draft.trim();
        if (!v) return;
        // Typing a real entry supersedes the "none" assertion — start from a
        // clean list so the sentinel doesn't linger alongside actual data.
        const base = isDenied ? [] : items;
        if (base.includes(v)) { setDraft(''); return; }
        onPatch({ [field]: [...base, v] });
        setDraft('');
    };

    const remove = (idx) => onPatch({ [field]: items.filter((_, i) => i !== idx) });

    const toggleDenied = () => onPatch({ [field]: isDenied ? [] : [denialValue] });

    return (
        <Box>
            <SectionHeading title={title} hint={hint} />
            {denialLabel && !disabled && (
                <Box sx={{ mb: 1.5 }}>
                    <Chip
                        clickable
                        onClick={toggleDenied}
                        icon={isDenied ? <CheckRoundedIcon /> : undefined}
                        label={denialLabel}
                        variant={isDenied ? 'filled' : 'outlined'}
                        sx={isDenied ? {
                            bgcolor: 'rgba(46,125,50,0.12)',
                            color: 'success.dark',
                            border: '1px solid rgba(46,125,50,0.4)',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'success.dark' },
                        } : {
                            borderColor: 'rgba(5,47,93,0.30)',
                            color: NAVY,
                            fontWeight: 600,
                        }}
                    />
                </Box>
            )}
            {!disabled && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={placeholder || 'Add entry…'}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                        InputProps={{
                            sx: {
                                bgcolor: '#fff',
                                '& fieldset': { borderColor: 'rgba(5,47,93,0.15)' },
                                '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: 1 },
                            },
                        }}
                    />
                    <Button
                        onClick={add}
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        sx={{
                            bgcolor: NAVY,
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                        }}
                    >
                        Add
                    </Button>
                </Stack>
            )}
            {isDenied ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                        label={denialValue}
                        onDelete={disabled ? undefined : toggleDenied}
                        sx={{
                            bgcolor: 'rgba(46,125,50,0.10)',
                            color: 'success.dark',
                            fontWeight: 500,
                            '& .MuiChip-deleteIcon': { color: 'rgba(46,125,50,0.55)' },
                            '&:hover .MuiChip-deleteIcon': { color: 'success.dark' },
                        }}
                    />
                </Stack>
            ) : items.length === 0 ? (
                <EmptyHint label={`No ${title.toLowerCase()} recorded yet.`} />
            ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {items.map((it, i) => (
                        <Chip
                            key={`${it}-${i}`}
                            label={it}
                            onDelete={disabled ? undefined : () => remove(i)}
                            sx={{
                                bgcolor: 'rgba(5,47,93,0.06)',
                                color: NAVY,
                                fontWeight: 500,
                                '& .MuiChip-deleteIcon': { color: 'rgba(5,47,93,0.45)' },
                                '&:hover .MuiChip-deleteIcon': { color: NAVY },
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function EmptyHint({ label }) {
    return (
        <Box
            sx={{
                py: 3,
                px: 2,
                borderRadius: 1.5,
                bgcolor: 'rgba(5,47,93,0.025)',
                border: '1px dashed rgba(5,47,93,0.15)',
                textAlign: 'center',
            }}
        >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
        </Box>
    );
}
